import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { registerHandler } from "@/lib/stepUpBus";
import { ShieldCheck, KeyRound, Fingerprint } from "lucide-react";
import {
    isPlatformAuthenticatorAvailable, isWebAuthnSupported,
    reauthenticateWithPasskey, passkeyButtonLabel,
} from "@/lib/webauthn";

/**
 * Global step-up prompt. Opens when the backend returns 428 with
 * `code: step_up_required` — the axios interceptor plugs into
 * `registerHandler` and awaits our resolver.
 *
 * Two paths:
 *   1. **Passkey / Touch ID** — invisible to the user beyond the platform
 *      biometric prompt; server issues the `step_up_token` cookie via
 *      `/webauthn/reauth-complete`.
 *   2. **6-digit TOTP** — fallback if no passkey is enrolled or the user
 *      taps "Use code instead".
 */
export default function StepUpModal() {
    const [open, setOpen] = useState(false);
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [passkeyReady, setPasskeyReady] = useState(false);
    const [mode, setMode] = useState("auto");  // auto | passkey | code
    const resolverRef = useRef(null);

    const closeWith = useCallback((value) => {
        setOpen(false);
        setCode("");
        setError("");
        setBusy(false);
        setMode("auto");
        if (resolverRef.current) {
            resolverRef.current(value);
            resolverRef.current = null;
        }
    }, []);

    useEffect(() => {
        const unregister = registerHandler(
            () => new Promise((resolve) => {
                resolverRef.current = resolve;
                setOpen(true);
            }),
        );
        return unregister;
    }, []);

    // On open: probe whether the user has a platform authenticator so we
    // can offer the passkey button.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            const supported = isWebAuthnSupported() && await isPlatformAuthenticatorAvailable();
            if (!cancelled) setPasskeyReady(supported);
        })();
        return () => { cancelled = true; };
    }, [open]);

    const submitCode = async (e) => {
        e?.preventDefault?.();
        setBusy(true);
        setError("");
        try {
            await api.post("/auth/2fa/step-up", { code });
            closeWith(true);
        } catch (err) {
            setError(formatApiError(err));
            setBusy(false);
        }
    };

    const submitPasskey = async () => {
        setBusy(true);
        setError("");
        try {
            await reauthenticateWithPasskey();
            closeWith(true);
        } catch (err) {
            const msg = err?.message || "Passkey verification cancelled.";
            if (/cancel/i.test(msg)) { setBusy(false); return; }
            setError(msg);
            setBusy(false);
        }
    };

    const showPasskey = passkeyReady && mode !== "code";

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) closeWith(false); }}>
            <DialogContent className="max-w-md" data-testid="step-up-modal">
                <DialogHeader>
                    <div className="flex items-center gap-2 text-emerald-700">
                        <ShieldCheck size={16} />
                        <DialogTitle className="text-base">Confirm this action</DialogTitle>
                    </div>
                    <DialogDescription className="text-[13px] text-stone-500 leading-relaxed">
                        This is a sensitive action. {showPasskey
                            ? "Use your passkey or Touch ID to continue — you won't have to do this again for a few minutes."
                            : "Enter the 6-digit code from your authenticator app to continue."}
                    </DialogDescription>
                </DialogHeader>

                {showPasskey ? (
                    <div className="mt-3 space-y-3">
                        <Button
                            type="button"
                            onClick={submitPasskey}
                            disabled={busy}
                            className="w-full bg-stone-900 hover:bg-stone-800 text-white h-11"
                            data-testid="step-up-passkey-btn"
                        >
                            <Fingerprint size={14} className="mr-2" />
                            {busy ? "Waiting for authenticator…" : passkeyButtonLabel().replace("Sign in with ", "Confirm with ")}
                        </Button>
                        {error && <div className="text-xs text-red-700" data-testid="step-up-error">{error}</div>}
                        <div className="flex items-center justify-between text-xs">
                            <button type="button" className="text-stone-500 hover:text-stone-800 underline"
                                onClick={() => setMode("code")} data-testid="step-up-use-code">
                                Use 6-digit code instead
                            </button>
                            <button type="button" className="text-stone-500 hover:text-stone-800"
                                onClick={() => closeWith(false)} disabled={busy} data-testid="step-up-cancel">
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submitCode} className="mt-2">
                        <label className="text-[11px] uppercase tracking-widest text-stone-500 flex items-center gap-1">
                            <KeyRound size={11} /> Authenticator code
                        </label>
                        <Input
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
                            placeholder="123456"
                            autoFocus
                            className="mt-1 font-mono-figure text-center text-lg tracking-[0.35em]"
                            data-testid="step-up-code-input"
                        />
                        {error && <div className="mt-2 text-xs text-red-700" data-testid="step-up-error">{error}</div>}
                        <div className="mt-4 flex items-center justify-between gap-2">
                            {passkeyReady && (
                                <button type="button" className="text-xs text-stone-500 hover:text-stone-800 underline"
                                    onClick={() => setMode("auto")} data-testid="step-up-use-passkey">
                                    Use passkey instead
                                </button>
                            )}
                            <div className="ml-auto flex gap-2">
                                <Button type="button" size="sm" variant="ghost" onClick={() => closeWith(false)} disabled={busy} data-testid="step-up-cancel">
                                    Cancel
                                </Button>
                                <Button
                                    type="submit" size="sm"
                                    disabled={busy || code.length < 6}
                                    className="bg-stone-900 hover:bg-stone-800 text-white"
                                    data-testid="step-up-submit"
                                >
                                    {busy ? "Verifying…" : "Confirm"}
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
